import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';

/**
 * The polite line that confirms a change ("Quoting is turned off."). It is a live region, so screen readers
 * hear the confirmation without focus moving away from where the person was.
 */
export function SavedNotice({ message }: { message: string | null }) {
  return (
    <p className="admin-notice" role="status" aria-live="polite" data-empty={message ? undefined : 'true'}>
      {message}
    </p>
  );
}

/** A visual loading placeholder with its words (the region it fills says it is busy). */
export function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="admin-loading" role="status">
      <span className="admin-spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

/**
 * A pager button. aria-disabled instead of disabled: a button that becomes unavailable after a page change
 * keeps the keyboard focus instead of dropping it on the document.
 */
export function PagerButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  const handle = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick();
  };
  return (
    <button type="button" className="admin-action" aria-disabled={disabled || undefined} onClick={handle}>
      {children}
    </button>
  );
}

/** The current time, updated every `intervalMs` (for countdowns such as the support access banner). */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}
